import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ClipboardList, MapPin, Building2, LayoutGrid, Image as ImageIcon, FileText, Box, BadgeCheck, UserRound,
  Check, Upload, Link2, X, Loader2, Globe, Sparkles, ExternalLink, Send, AlertCircle, Clock, CalendarRange,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { UbicacionObraPicker, UbicacionObraModal, CambiarUbicacionLink } from '../obras/components/UbicacionObra';
import {
  CATEGORIAS, MAX_POR_CATEGORIA, getDatosObra, guardarDatos, subirArchivo, quitarArchivo, agregarLink, confirmarDatos,
  problemaArchivo, tamano, esImagen, errorDatos, extension,
} from './datosObra.api';
import { direccionDesdeCoords } from '../paginaWeb/paginaWeb.api';
import { formatDateTime24 } from '../utils/formatTime24';
import { JornadaLaboral, CronogramaObra } from './JornadaYCronograma';

/* Obra › Datos de la obra: el cliente carga todo lo de su proyecto en un solo lugar (ubicación exacta, planos,
   renders, brochure, modelo 3D, logo, datos y contacto). Con eso armamos su página web (Marketing › Página web
   lee lo mismo y no lo vuelve a pedir) y la IA entiende mejor la obra. Al confirmar, le llega un mail a Jairo. */

const ICONOS = { ubicacion: MapPin, proyecto: Building2, jornada: Clock, cronograma: CalendarRange, planos: LayoutGrid, renders: ImageIcon, brochure: FileText, modelo3d: Box, logo: BadgeCheck, contacto: UserRound };
const ITEMS = [
  ['ubicacion', 'Ubicación exacta'], ['proyecto', 'Datos del proyecto'], ['planos', 'Planos'], ['renders', 'Renders'],
  ['brochure', 'Brochure'], ['modelo3d', 'Modelo 3D'], ['logo', 'Logo'], ['contacto', 'Contacto de obra'],
];
const ETAPAS = ['Preventa / pozo', 'Movimiento de suelo', 'Fundaciones', 'Estructura', 'Albañilería', 'Instalaciones', 'Terminaciones', 'Entrega'];
const CAMPOS_PROYECTO = [
  { k: 'nombreComercial', label: 'Nombre comercial', ph: 'Ej: Torre Gaona', full: true, req: true },
  { k: 'direccion', label: 'Dirección', ph: 'Calle y número, barrio, ciudad', full: true, req: true },
  { k: 'desarrolladora', label: 'Desarrolladora', ph: 'Quién desarrolla', req: true },
  { k: 'constructora', label: 'Constructora', ph: 'Quién construye' },
  { k: 'pisos', label: 'Cantidad de pisos', ph: 'Ej: 12', tipo: 'num' },
  { k: 'unidades', label: 'Unidades', ph: 'Ej: 48', tipo: 'num' },
  { k: 'm2', label: 'Superficie (m²)', ph: 'Ej: 5200', tipo: 'num' },
  { k: 'etapa', label: 'Etapa actual', tipo: 'etapa', req: true },
  { k: 'inicio', label: 'Fecha de inicio', tipo: 'month' },
  { k: 'finEstimado', label: 'Fin estimado', tipo: 'month', req: true },
  { k: 'web', label: 'Web', ph: 'www.tuproyecto.com' },
  { k: 'instagram', label: 'Instagram', ph: '@tuproyecto' },
  { k: 'otrasRedes', label: 'Otras redes', ph: 'LinkedIn, Facebook, YouTube…', full: true },
  { k: 'descripcion', label: 'Descripción', ph: 'Amenities, tipologías, lo que lo hace especial. También podés pegar links.', tipo: 'area', full: true },
];
const CAMPOS_CONTACTO = [
  { k: 'nombre', label: 'Nombre y apellido', ph: 'Quién está en la obra', req: true },
  { k: 'cargo', label: 'Cargo', ph: 'Ej: Jefe de obra' },
  { k: 'whatsapp', label: 'WhatsApp', ph: '+54 9 11 1234 5678', tipo: 'tel' },
  { k: 'email', label: 'Mail', ph: 'nombre@empresa.com', tipo: 'email' },
];
const fechaLarga = (iso) => { try { return formatDateTime24(iso, { dateOptions: { day: 'numeric', month: 'long' } }); } catch (e) { return ''; } };
const aMes = (v) => (v ? String(v).slice(0, 7) : '');

function Chip({ ok, texto }) {
  return ok
    ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full whitespace-nowrap"><Check size={11} />{texto || 'Cargado'}</span>
    : texto === 'Opcional' || texto === 'Por defecto'
      ? <span className="text-[11px] font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full whitespace-nowrap">{texto}</span>
      : <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full whitespace-nowrap">{texto || 'Falta'}</span>;
}

function Seccion({ id, titulo, desc, ok, chip, children, accion }) {
  const Icon = ICONOS[id] || ClipboardList;
  return (
    <section id={`do-${id}`} className="bg-white border border-gray-200 rounded-2xl p-4 md:p-6 space-y-4 scroll-mt-24">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}><Icon size={19} /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="font-bold text-gray-800 text-base md:text-lg">{titulo}</h3>
            <Chip ok={ok} texto={chip} />
          </div>
          {desc && <p className="text-sm text-gray-500 mt-0.5">{desc}</p>}
        </div>
      </div>
      {children}
      {accion}
    </section>
  );
}

function Campo({ c, valor, sugerido, onChange }) {
  const base = 'block w-full border border-gray-300 rounded-lg px-3 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';
  const id = `do-campo-${c.k}`;
  return (
    <div className={`space-y-1 ${c.full ? 'sm:col-span-2' : ''}`}>
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className="text-sm font-semibold text-gray-700">{c.label}{c.req && <span className="text-gray-400 font-normal"> *</span>}</label>
        {sugerido && <span className="text-[11px] font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">Lo tenemos · confirmalo</span>}
      </div>
      {c.tipo === 'area' ? (
        <textarea id={id} value={valor} onChange={(e) => onChange(e.target.value)} placeholder={c.ph} className={`${base} py-2 resize-y`} style={{ height: 110, lineHeight: 1.5 }} />
      ) : c.tipo === 'etapa' ? (
        <select id={id} value={valor} onChange={(e) => onChange(e.target.value)} className={base} style={{ height: 42 }}>
          <option value="">Elegí la etapa</option>
          {ETAPAS.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      ) : (
        <input id={id} type={c.tipo === 'month' ? 'month' : c.tipo === 'num' ? 'text' : c.tipo || 'text'} inputMode={c.tipo === 'num' ? 'decimal' : undefined} value={valor} onChange={(e) => onChange(e.target.value)} placeholder={c.ph} className={base} style={{ height: 42 }} />
      )}
    </div>
  );
}

function Vista({ a, onQuitar, quitando }) {
  const img = esImagen(a) && !a.link;
  return (
    <li className="group relative bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
      <a href={a.url} target="_blank" rel="noopener noreferrer" className="block aspect-[4/3] bg-gray-50 overflow-hidden" title={`Abrir ${a.nombre}`}>
        {img ? <img src={a.url} alt={a.nombre} loading="lazy" className="w-full h-full object-cover" />
          : <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-gray-500">
              {a.link ? <Link2 size={26} /> : <FileText size={26} />}
              <span className="text-[11px] font-bold uppercase tracking-wide">{a.link ? 'link' : extension(a.nombre) || 'archivo'}</span>
            </div>}
      </a>
      <div className="px-2 py-1.5 text-xs">
        <div className="truncate text-gray-700 font-medium" title={a.nombre}>{a.nombre}</div>
        <div className="text-gray-400 flex items-center gap-1">{a.link ? 'Link externo' : a.bytes ? tamano(a.bytes) : ''}{a.origen === 'pagina-web' && <span className="text-blue-600">· desde Página web</span>}</div>
      </div>
      <button type="button" onClick={() => onQuitar(a)} disabled={quitando} aria-label={`Quitar ${a.nombre}`} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/95 border border-gray-200 text-gray-600 hover:text-red-600 flex items-center justify-center shadow-sm">
        {quitando ? <Loader2 size={13} className="animate-spin" /> : <X size={14} />}
      </button>
    </li>
  );
}

/** Zona de arrastrar y soltar de una categoría, con vista previa de lo subido. Se reusa en Página web. */
export function ZonaArchivos({ feedId, cat, archivos, onFicha, origen = 'datos-obra', compacta = false }) {
  const c = CATEGORIAS[cat];
  const input = useRef(null);
  const [encima, setEncima] = useState(false);
  const [subiendo, setSubiendo] = useState(null); // { nombre, pct, faltan }
  const [quitando, setQuitando] = useState('');
  const [link, setLink] = useState('');
  const [verLink, setVerLink] = useState(false);

  const tomar = async (lista) => {
    const fs = Array.from(lista || []);
    if (!fs.length) return;
    const lugar = MAX_POR_CATEGORIA - archivos.length;
    if (cat === 'logo') fs.splice(1);
    else if (fs.length > lugar) { toast.error(`Podés subir hasta ${MAX_POR_CATEGORIA} archivos en ${c.nombre}.`); fs.splice(Math.max(0, lugar)); }
    for (let i = 0; i < fs.length; i++) {
      const f = fs[i];
      const p = problemaArchivo(cat, f);
      if (p) { toast.error(p); continue; }
      setSubiendo({ nombre: f.name, pct: 0, faltan: fs.length - i - 1 });
      try {
        const ficha = await subirArchivo(feedId, cat, f, { origen, onProgress: (b) => setSubiendo((s) => s && { ...s, pct: Math.min(100, Math.round((b / Math.max(1, f.size)) * 100)) }) });
        onFicha(ficha);
      } catch (e) {
        toast.error(`No pudimos subir "${f.name}": ${errorDatos(e)}`);
      }
    }
    setSubiendo(null);
  };
  const quitar = async (a) => {
    setQuitando(a.id);
    try { onFicha(await quitarArchivo(feedId, a.id)); } catch (e) { toast.error(errorDatos(e)); }
    setQuitando('');
  };
  const sumarLink = async () => {
    const u = link.trim();
    if (!/^https:\/\//i.test(u)) { toast.error('Pegá un link que empiece con https://'); return; }
    try { onFicha(await agregarLink(feedId, cat, u)); setLink(''); setVerLink(false); } catch (e) { toast.error(errorDatos(e)); }
  };

  return (
    <div className="space-y-3">
      {archivos.length > 0 && (
        <ul className={`grid gap-2 ${compacta ? 'grid-cols-3 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'}`}>
          {archivos.map((a) => <Vista key={a.id} a={a} onQuitar={quitar} quitando={quitando === a.id} />)}
        </ul>
      )}
      <div
        onDragOver={(e) => { e.preventDefault(); setEncima(true); }}
        onDragLeave={() => setEncima(false)}
        onDrop={(e) => { e.preventDefault(); setEncima(false); if (!subiendo) tomar(e.dataTransfer.files); }}
        onClick={() => !subiendo && input.current && input.current.click()}
        role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.current && input.current.click(); } }}
        className={`rounded-xl border-2 border-dashed px-4 ${compacta ? 'py-3' : 'py-5'} text-center cursor-pointer transition ${encima ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`}>
        {subiendo ? (
          <div className="space-y-1.5">
            <div className="text-sm text-gray-700 inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin text-blue-600" />Subiendo {subiendo.nombre}… {subiendo.pct}%{subiendo.faltan ? ` · faltan ${subiendo.faltan}` : ''}</div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden max-w-xs mx-auto"><div className="h-full bg-blue-600 transition-all" style={{ width: `${subiendo.pct}%` }} /></div>
          </div>
        ) : (
          <>
            <div className="text-sm font-medium text-blue-700 inline-flex items-center gap-1.5"><Upload size={15} />{cat === 'logo' && archivos.length ? 'Cambiar el logo' : 'Arrastrá los archivos o tocá para elegir'}</div>
            <div className="text-xs text-gray-500 mt-0.5">{c.ext.map((x) => x.toUpperCase()).join(', ')} · hasta {Math.round(c.max / 1048576)} MB {cat === 'logo' ? '' : 'cada uno'}</div>
          </>
        )}
        <input ref={input} type="file" className="sr-only" accept={c.accept} multiple={cat !== 'logo'} onChange={(e) => { tomar(e.target.files); e.target.value = ''; }} />
      </div>
      {c.link && (
        verLink ? (
          <div className="flex flex-col sm:flex-row gap-2">
            <input type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://drive.google.com/… o WeTransfer, visor 3D" aria-label={`Link de ${c.nombre}`} className="flex-1 border border-gray-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ height: 40 }} />
            <div className="flex gap-2">
              <button type="button" onClick={sumarLink} className="px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold" style={{ height: 40 }}>Agregar</button>
              <button type="button" onClick={() => { setVerLink(false); setLink(''); }} className="px-3 rounded-lg text-sm text-gray-600 hover:bg-gray-100" style={{ height: 40 }}>Cancelar</button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => setVerLink(true)} className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1.5"><Link2 size={14} />¿Es muy pesado? Pegá un link</button>
        )
      )}
    </div>
  );
}

// Ubicación exacta: el selector de la obra (feature/ubicacion-obra) que guarda en webback
// (PUT /api/camaras/:feed/obra/ubicacion → obras.ubicacionMaps), la misma que usan Clima, Dashboard IA y Calendario.
function Ubicacion({ feedId, cam, ficha, camara, onGuardada }) {
  const actual = ficha?.ubicacion || null;
  const [cambiar, setCambiar] = useState(false);
  if (!actual) {
    return <UbicacionObraPicker feedId={feedId} cam={cam} initial={camara} title="Marcá dónde está tu obra" onSaved={() => { toast.success('Guardamos la ubicación de la obra'); onGuardada(); }} />;
  }
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      <a href={`https://www.google.com/maps?q=${actual.lat},${actual.lng}`} target="_blank" rel="noopener noreferrer" className="block shrink-0 w-full sm:w-56 h-32 rounded-xl overflow-hidden border border-gray-200 bg-gray-100" title="Ver en Google Maps">
        <img alt="Mapa de la ubicación de la obra" className="w-full h-full object-cover" src={`https://tile.openstreetmap.org/16/${Math.floor(((actual.lng + 180) / 360) * 65536)}/${Math.floor(((1 - Math.log(Math.tan((actual.lat * Math.PI) / 180) + 1 / Math.cos((actual.lat * Math.PI) / 180)) / Math.PI) / 2) * 65536)}.png`} />
      </a>
      <div className="min-w-0 text-sm text-gray-700">
        <div className="inline-flex items-start gap-1.5"><MapPin size={15} className="text-blue-600 mt-0.5 shrink-0" /><span>{actual.direccion || 'Ubicación marcada en el mapa'}</span></div>
        <div className="text-xs text-gray-500 mt-0.5">{actual.lat.toFixed(5)}, {actual.lng.toFixed(5)}</div>
        <CambiarUbicacionLink onClick={() => setCambiar(true)} className="mt-2" />
      </div>
      <UbicacionObraModal open={cambiar} onClose={() => setCambiar(false)} feedId={feedId} cam={cam} initial={actual} direccion={actual.direccion || ''} onSaved={() => { setCambiar(false); toast.success('Actualizamos la ubicación'); onGuardada(); }} />
    </div>
  );
}

export default function DatosObra({ user, customerInfo, identifier, onIrA }) {
  const feedId = user?.feedID;
  const [ficha, setFicha] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ proyecto: {}, contacto: {} });
  const [sugeridos, setSugeridos] = useState({});
  const [guardando, setGuardando] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const camara = user?.coordinates?.lat ? { lat: +user.coordinates.lat, lng: +user.coordinates.lng } : null;

  // Lo que ya sabemos de la cámara y de la empresa se ofrece para confirmar (no se guarda hasta que el cliente guarda).
  const sugerencias = useMemo(() => ({
    proyecto: { nombreComercial: user?.obraName || user?.Name || '', desarrolladora: customerInfo?.name || '', web: customerInfo?.website || customerInfo?.web || customerInfo?.webUrl || '', instagram: customerInfo?.instagramUrl || customerInfo?.instagram || '' },
    contacto: { whatsapp: customerInfo?.mainUserPhone || '', email: customerInfo?.mainUserEmail || '' },
  }), [user, customerInfo]);

  const aplicar = useCallback((f, { pisar = true } = {}) => {
    setFicha(f);
    setForm((prev) => {
      const sug = {};
      const arma = (grupo, campos) => {
        const o = {};
        for (const { k } of campos) {
          const guardado = f?.[grupo]?.[k] || '';
          const v = !pisar && prev[grupo][k] !== undefined && prev[grupo][k] !== (prev._base?.[grupo]?.[k] ?? '') ? prev[grupo][k] : guardado || sugerencias[grupo]?.[k] || (grupo === 'proyecto' && k === 'direccion' ? f?.ubicacion?.direccion || '' : '');
          if (!guardado && v && (sugerencias[grupo]?.[k] === v || (k === 'direccion' && f?.ubicacion?.direccion === v))) sug[`${grupo}.${k}`] = true;
          o[k] = k === 'inicio' || k === 'finEstimado' ? aMes(v) : v;
        }
        return o;
      };
      const proyecto = arma('proyecto', CAMPOS_PROYECTO);
      const contacto = arma('contacto', CAMPOS_CONTACTO);
      setSugeridos(sug);
      return { proyecto, contacto, _base: { proyecto: { ...(f?.proyecto || {}) }, contacto: { ...(f?.contacto || {}) } } };
    });
  }, [sugerencias]);

  const cargar = useCallback(async (fresco = false) => {
    if (!feedId) return;
    try { aplicar(await getDatosObra(feedId, { fresco })); setError(''); } catch (e) { setError(errorDatos(e)); }
  }, [feedId, aplicar]);
  useEffect(() => { cargar(true); }, [cargar]);

  // si no hay dirección cargada, la sugerimos desde la ubicación de la cámara
  useEffect(() => {
    if (!ficha || form.proyecto.direccion || !camara) return;
    direccionDesdeCoords(camara.lat, camara.lng).then((d) => {
      if (d) { setForm((f) => (f.proyecto.direccion ? f : { ...f, proyecto: { ...f.proyecto, direccion: d } })); setSugeridos((s) => ({ ...s, 'proyecto.direccion': true })); }
    });
  }, [ficha]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (grupo, k) => (v) => { setForm((f) => ({ ...f, [grupo]: { ...f[grupo], [k]: v } })); setSugeridos((s) => ({ ...s, [`${grupo}.${k}`]: false })); };
  const pendientes = (grupo, campos) => {
    const o = {};
    for (const { k } of campos) if ((form[grupo][k] || '') !== aMes0(k, ficha?.[grupo]?.[k])) o[k] = form[grupo][k] || '';
    return o;
  };
  const aMes0 = (k, v) => (k === 'inicio' || k === 'finEstimado' ? aMes(v) : v || '');
  const cambiosProyecto = ficha ? pendientes('proyecto', CAMPOS_PROYECTO) : {};
  const cambiosContacto = ficha ? pendientes('contacto', CAMPOS_CONTACTO) : {};
  const hayCambios = Object.keys(cambiosProyecto).length + Object.keys(cambiosContacto).length > 0;

  const guardar = async (grupo) => {
    const cambios = grupo === 'proyecto' ? { proyecto: cambiosProyecto } : grupo === 'contacto' ? { contacto: cambiosContacto } : { proyecto: cambiosProyecto, contacto: cambiosContacto };
    setGuardando(grupo);
    try {
      const f = await guardarDatos(feedId, cambios);
      aplicar(f, { pisar: false }); // no perder lo que esté escribiendo en la otra sección
      if (grupo !== 'todo') toast.success('Guardado');
      return true;
    } catch (e) {
      toast.error(errorDatos(e));
      return false;
    } finally {
      setGuardando('');
    }
  };
  const onFicha = (f) => aplicar(f, { pisar: false });

  if (!feedId) return <div className="bg-white border border-gray-200 rounded-2xl p-6 text-gray-600">Esta cámara no tiene una obra asociada.</div>;
  if (error && !ficha) return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 text-gray-700 flex items-start gap-3"><AlertCircle className="text-amber-600 shrink-0" size={20} />
      <div><div className="font-semibold">No pudimos traer los datos de tu obra.</div><div className="text-sm text-gray-500">{error}</div><button type="button" onClick={() => cargar(true)} className="mt-2 text-sm text-blue-700 hover:underline">Probar de nuevo</button></div>
    </div>
  );
  if (!ficha) return <div className="bg-white border border-gray-200 rounded-2xl p-6 text-gray-500 inline-flex items-center gap-2 w-full"><Loader2 size={16} className="animate-spin" />Cargando los datos de tu obra…</div>;

  // El logo que ya tenemos de la empresa cuenta como cargado (no hace falta subirlo de nuevo).
  const logoEmpresa = customerInfo?.logo || user?.logo || '';
  const estado = { ...(ficha.progreso?.estado || {}) };
  if (!estado.logo && logoEmpresa) estado.logo = true;
  const cargados = ITEMS.filter(([k]) => estado[k]).length;
  const pct = Math.round((cargados / ITEMS.length) * 100);
  const confirmadoVigente = ficha.confirmadoAt && !(ficha.actualizadoAt && ficha.actualizadoAt > ficha.confirmadoAt);

  const confirmar = async () => {
    setConfirmando(true);
    try {
      if (hayCambios && !(await guardar('todo'))) return;
      const f = await confirmarDatos(feedId);
      aplicar(f);
      toast.success('¡Listo! Le avisamos al equipo de Lisual.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(errorDatos(e));
    } finally {
      setConfirmando(false);
    }
  };
  const ir = (k) => { const el = document.getElementById(`do-${k}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  const botonGuardar = (grupo, cambios) => Object.keys(cambios).length > 0 && (
    <div className="flex justify-end">
      <button type="button" onClick={() => guardar(grupo)} disabled={!!guardando} className="inline-flex items-center gap-2 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60" style={{ height: 40 }}>
        {guardando === grupo ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}Guardar
      </button>
    </div>
  );

  return (
    <div className="space-y-5 md:space-y-6 pb-24 md:pb-0">
      <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full"><ClipboardList size={13} />Datos de la obra</div>

      {/* para qué sirve + progreso */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden grid md:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
        <div className="p-5 md:p-7 space-y-3">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 leading-tight">Contanos todo sobre tu obra</h2>
          <p className="text-gray-600">Con estos datos <b className="text-gray-800">armamos la página web de tu obra</b> y <b className="text-gray-800">la IA entiende mejor tu proyecto</b>: entradas y salidas, tiempos, efectividad, clima… Lo cargás una sola vez y lo usamos en todo LisualPro.</p>
          <ul className="grid sm:grid-cols-2 gap-2 text-sm text-gray-700">
            <li className="flex gap-2"><Globe size={16} className="text-blue-600 shrink-0 mt-0.5" />Tu página web sale con renders, planos, brochure y tu 3D.</li>
            <li className="flex gap-2"><Sparkles size={16} className="text-blue-600 shrink-0 mt-0.5" />La IA compara el avance con la etapa y las fechas que nos des.</li>
          </ul>
        </div>
        <div className="p-5 md:p-7 bg-gray-50 border-t md:border-t-0 md:border-l border-gray-200 space-y-3">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-lg font-bold text-gray-800">Tu obra está {pct}% completa</div>
            <div className="text-sm text-gray-500">{cargados} de {ITEMS.length}</div>
          </div>
          <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Progreso de los datos de la obra"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>
          <ul className="flex flex-wrap gap-1.5">
            {ITEMS.map(([k, n]) => (
              <li key={k}><button type="button" onClick={() => ir(k)} className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-1 border ${estado[k] ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400'}`}>{estado[k] ? <Check size={12} /> : <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}{n}</button></li>
            ))}
          </ul>
          {confirmadoVigente
            ? <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex gap-2"><BadgeCheck size={16} className="shrink-0 mt-0.5" />Confirmaste los datos el {fechaLarga(ficha.confirmadoAt)} · Si cambiás algo, volvé a confirmar.</div>
            : ficha.importadoDe ? <div className="text-xs text-gray-500">Sumamos lo que ya nos habías mandado desde Página web.</div> : null}
        </div>
      </div>

      <Seccion id="ubicacion" titulo="Ubicación exacta" ok={!!estado.ubicacion} desc="Marcá en el mapa dónde está la obra. La usamos para el clima, la llegada en 3D de tu web y la IA.">
        <Ubicacion feedId={feedId} cam={identifier || user?.identifier} ficha={ficha} camara={camara} onGuardada={() => cargar(true)} />
      </Seccion>

      <Seccion id="proyecto" titulo="Datos del proyecto" ok={!!estado.proyecto} desc="Los campos con * son los que más usamos."
        accion={botonGuardar('proyecto', cambiosProyecto)}>
        <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
          {CAMPOS_PROYECTO.map((c) => <Campo key={c.k} c={c} valor={form.proyecto[c.k] || ''} sugerido={sugeridos[`proyecto.${c.k}`]} onChange={set('proyecto', c.k)} />)}
        </div>
      </Seccion>

      <Seccion id="jornada" titulo="Jornada laboral" ok={!!ficha.jornada} chip={ficha.jornada ? 'Cargada' : 'Por defecto'} desc="Qué días y en qué horario se trabaja en la obra. Lo usamos para el clima, las alertas y la IA.">
        <JornadaLaboral feedId={feedId} ficha={ficha} onFicha={onFicha} />
      </Seccion>

      <Seccion id="cronograma" titulo="Cronograma de obra (Gantt)" ok={!!ficha.archivos.cronograma?.length} chip={ficha.archivos.cronograma?.length ? (ficha.cronograma?.estado === 'listo' ? `${ficha.cronograma.actividades.length} actividades` : 'Cargado') : 'Opcional'}
        desc="Si tenés el diagrama de Gantt o el archivo de MS Project, subilo: el Clima te va a avisar qué días conviene hormigonar, excavar o impermeabilizar según lo que toca en tu cronograma.">
        <CronogramaObra feedId={feedId} ficha={ficha} onFicha={onFicha} />
      </Seccion>

      {['planos', 'renders', 'brochure', 'modelo3d'].map((cat) => (
        <Seccion key={cat} id={cat} titulo={CATEGORIAS[cat].nombre} ok={!!estado[cat]} chip={ficha.archivos[cat].length ? `${ficha.archivos[cat].length} cargado${ficha.archivos[cat].length > 1 ? 's' : ''}` : undefined} desc={CATEGORIAS[cat].desc}>
          <ZonaArchivos feedId={feedId} cat={cat} archivos={ficha.archivos[cat]} onFicha={onFicha} />
        </Seccion>
      ))}

      <Seccion id="logo" titulo="Logo de la empresa" ok={!!estado.logo} chip={!ficha.archivos.logo.length && logoEmpresa ? 'Ya lo tenemos' : undefined} desc={CATEGORIAS.logo.desc}>
        {!ficha.archivos.logo.length && logoEmpresa && (
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <div className="w-28 h-14 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden"><img src={logoEmpresa} alt="Logo actual" className="max-h-12 max-w-[100px] object-contain" /></div>
            Usamos el logo que ya tenemos. Si querés otro, subilo abajo.
          </div>
        )}
        <ZonaArchivos feedId={feedId} cat="logo" archivos={ficha.archivos.logo} onFicha={onFicha} />
      </Seccion>

      <Seccion id="contacto" titulo="Contacto de obra" ok={!!estado.contacto} desc="Con quién hablamos por temas de la obra (cámara, visitas, avisos)."
        accion={botonGuardar('contacto', cambiosContacto)}>
        <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
          {CAMPOS_CONTACTO.map((c) => <Campo key={c.k} c={c} valor={form.contacto[c.k] || ''} sugerido={sugeridos[`contacto.${c.k}`]} onChange={set('contacto', c.k)} />)}
        </div>
      </Seccion>

      {/* confirmar */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="text-sm text-gray-600">
          {pct < 100 ? <>Te falta: <b className="text-gray-800">{ITEMS.filter(([k]) => !estado[k]).map(([, n]) => n).join(', ')}</b>. Podés confirmar igual y sumarlo después.</> : <>¡Está todo! Confirmá y le avisamos al equipo.</>}
          {hayCambios && <span className="block text-xs text-amber-700 mt-1">Tenés cambios sin guardar: se guardan al confirmar.</span>}
          {onIrA && <button type="button" onClick={() => onIrA('Página web')} className="block mt-1 text-blue-700 hover:underline inline-flex items-center gap-1">Ver mi página web<ExternalLink size={13} /></button>}
        </div>
        <button type="button" onClick={confirmar} disabled={confirmando || !!guardando} className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60 md:min-w-[260px]">
          {confirmando ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}Confirmar datos de mi obra
        </button>
      </div>
    </div>
  );
}
